import React, { useEffect, useState } from 'react';
import { Trophy, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { playEpicFanfare, playCorrectSound } from '../FluencyDrill/sounds';

const INDIGO_COLORS = ['#6366f1', '#818cf8', '#a78bfa', '#c084fc', '#e879f9'];

const AUTO_REDIRECT_SECONDS = 15;

interface ResultScreenProps {
  currentSessionBestStreak: number;
  allTimeBestStreak: number | null; // from DB, null if first session
  levelUpCount: number; // total practice facts mastered across all levels
}

const ResultScreen: React.FC<ResultScreenProps> = ({
  currentSessionBestStreak,
  allTimeBestStreak,
  levelUpCount,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(AUTO_REDIRECT_SECONDS);

  const isNewRecord =
    currentSessionBestStreak > 0 &&
    (allTimeBestStreak === null || currentSessionBestStreak > allTimeBestStreak);

  // Sounds and confetti on mount
  useEffect(() => {
    if (isNewRecord) {
      playEpicFanfare();

      // Triple confetti bursts at 0ms, 300ms, 600ms (matching FluencyDrill pattern)
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: INDIGO_COLORS });
      const t1 = setTimeout(() => {
        confetti({ particleCount: 60, spread: 100, origin: { y: 0.5 }, colors: INDIGO_COLORS });
      }, 300);
      const t2 = setTimeout(() => {
        confetti({ particleCount: 100, spread: 120, origin: { y: 0.4 }, colors: INDIGO_COLORS });
      }, 600);

      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      playCorrectSound();

      // Single confetti burst
      confetti({ particleCount: 60, spread: 70, origin: { y: 0.6 }, colors: INDIGO_COLORS });
    }
  }, []);

  // Auto-redirect countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = '/';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
      <div className="max-w-md mx-auto space-y-6">
        {/* Section 1: Streak Hero Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="relative overflow-hidden rounded-2xl p-8 text-center bg-white border border-indigo-100 shadow-lg shadow-indigo-100/50"
        >
          {/* Decorative corner accents */}
          <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-bl from-indigo-50 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-50 to-transparent rounded-tr-full" />

          {isNewRecord ? (
            <div className="relative">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.3, 1] }}
                transition={{ type: 'spring', damping: 8, stiffness: 150 }}
              >
                <Trophy className="w-11 h-11 text-yellow-500 mx-auto mb-2" />
              </motion.div>
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                New Best Streak!
              </p>
              <span className="block text-7xl font-black bg-gradient-to-b from-indigo-500 to-purple-600 text-transparent bg-clip-text leading-tight">
                {currentSessionBestStreak}
              </span>
              <p className="text-base font-bold text-indigo-500 mt-3">
                {allTimeBestStreak !== null
                  ? `You beat your best of ${allTimeBestStreak}!`
                  : 'You set your first streak record!'}
              </p>
            </div>
          ) : (
            <div className="relative">
              <Trophy className="w-11 h-11 text-indigo-200 mx-auto mb-2" />
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                Session Streak
              </p>
              <span className="block text-4xl font-black text-indigo-400 leading-tight">
                {currentSessionBestStreak}
              </span>
              <p className="text-sm text-indigo-300 mt-3">
                Best: {allTimeBestStreak ?? '--'}
              </p>
            </div>
          )}
        </motion.div>

        {/* Section 2: Level-Up Summary */}
        {levelUpCount > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3 rounded-xl p-4 bg-white border border-indigo-100"
          >
            <Zap className="w-5 h-5 text-yellow-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-700">
              {levelUpCount} fact{levelUpCount !== 1 ? 's' : ''} leveled up
            </span>
          </motion.div>
        )}

        {/* Section 3: Auto-redirect countdown */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
            <span>Returning to dashboard in</span>
            <span className="font-bold">{timeRemaining}s</span>
          </div>
          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all duration-1000"
              style={{
                width: `${(timeRemaining / AUTO_REDIRECT_SECONDS) * 100}%`,
              }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultScreen;
